import Model from 'src/app/common/model';
import SpeciesTypeModel from './speciesType.model';

/**
 * @model BlindFeedSettingModel
 * @description
 * Represents the data structure for "Blind Feed" configuration settings within the AquaCloud application.
 *
 * **Domain Context:**
 * "Blind Feeding" in aquaculture refers to a feeding strategy where feed quantities are determined
 * by pre-calculated tables or schedules based on the days of culture (DOC) and stocking density,
 * rather than real-time appetite feedback (like check trays). This is common in the early stages
 * of culture (e.g., the first 30 days) when animals are too small to provide visible feeding feedback.
 *
 * **Purpose:**
 * This model serves as a Data Transfer Object (DTO) to define these feeding rules for specific species.
 * It maps a species (via `species_type_id`) to a set of feed quantity rules broken down by time intervals
 * (Initial, 1-7 days, 7-14 days, 14-30 days).
 *
 * **Key Responsibilities:**
 * 1. **Species Association**: Links feeding rules to a specific aquatic species (e.g., Vannamei, Monodon).
 * 2. **Stage-based Rationing**: Defines feed quantities for critical early growth stages.
 * 3. **Calculation Basis**: Provides the `count` property, likely used as a denominator or multiplier
 *    (e.g., feed per 100,000 post-larvae).
 *
 * **Data Flow:**
 * - **Configuration**: Administrators or Farm Managers define these settings in the "Blind Feed Settings" UI.
 * - **Storage**: The data is sent to the backend via `BlindFeedSettingsService` to be persisted in the database.
 * - **Usage**: When a new culture starts, the system uses these settings to generate daily feed recommendations
 *   automatically for the first month of culture.
 *
 * **User Roles & Permissions:**
 * - **Admin/Manager**: Can create, update, or delete these settings.
 * - **Technician**: Consumes these settings indirectly via generated feed schedules.
 *
 * **Features:**
 * - Supports granular time intervals (Weekly/Bi-weekly) for the first month.
 * - Strongly typed association with `SpeciesTypeModel`.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class BlindFeedSettingModel extends Model {
  /**
   * @type {SpeciesTypeModel}
   * @description
   * The full object representation of the species associated with this setting.
   * Populated during retrieval operations to display species names/details in the UI.
   */
  speciesType: SpeciesTypeModel;

  /**
   * @type {number}
   * @description
   * The base count or stocking density unit for the feed calculation.
   * For example, this might represent "per 100,000 PLs" or simply the stocking count
   * relevant to the formula.
   */
  count: number;

  /**
   * @type {number}
   * @description
   * The initial feed quantity (in kg or relevant UOM) to be applied immediately upon stocking (Day 0).
   * @optional
   */
  initial_feed?: number;

  /**
   * @type {number}
   * @description
   * The daily feed quantity (or total, depending on business logic) allocated for the
   * first week of culture (Days 1 through 7).
   * @optional
   */
  one_to_seven_days_feed?: number;

  /**
   * @type {number}
   * @description
   * The feed quantity allocated for the second week of culture (Days 7 through 14).
   * As the biomass increases, this value typically increases relative to the first week.
   * @optional
   */
  seven_to_fourteen_days_feed?: number;

  /**
   * @type {number}
   * @description
   * The feed quantity allocated for the period from Day 14 to Day 30.
   * This covers the transition from early post-larval stages to juvenile stages.
   * @optional
   */
  fourteen_to_thirty_days_feed?: number;

  /**
   * @type {number}
   * @description
   * The unique identifier (Foreign Key) of the species type this setting applies to.
   * Used for linking the setting to the `SpeciesType` table in the database.
   */
  species_type_id: number;

  /**
   * @constructor
   * @description
   * Initializes a new instance of the BlindFeedSettingModel.
   * Calls the parent `Model` constructor to initialize base properties.
   */
  constructor() {
    super();
  }
}
